import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRefs, watch, withDefaults } from 'vue';
import type { ContentCollection, NewContentCollection } from '@ilteducation/content-collections-types';
import type { Language } from '@ilteducation/content-ref-data-types';
import { cloneDeep } from 'lodash';
import { useRouter } from 'vue-router/composables';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import CoverArrayInput from '@/components/collections/CoverArrayInput.vue';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { getAllRefData } from '@/data-provider/refDataProvider';
import useCollectionUtils from '@/resources/collections/use-collection-utils';
import { create, update } from '@/data-provider/contentCollections';
import eventBus from '@/foundations/services/eventBus';
import RichTextInput from '@/components/RichTextInput.vue';
import IconPreview from '@/components/IconPreview.vue';

type Props = {
  id?: string;
  title: string;
  item?: ContentCollection | NewContentCollection;
  isCreate?: boolean;
  isClone?: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: null,
    title: null,
    item: null,
    isCreate: { type: Boolean, default: false },
    isClone: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { id?: string, title: string, item?: ContentCollection | NewContentCollection, isCreate: boolean, isClone: boolean }


const { id, title, item, isCreate, isClone } = toRefs(props);

const router = useRouter();
const { CollectionsAvailableIcons, icons, patchCollection } = useCollectionUtils();

// form related
const form = ref();
const isFormValid = ref(false);
const originalItem = ref<ContentCollection>(cloneDeep(item.value));
const contentCollection = ref<ContentCollection | NewContentCollection>({
  ...item.value,
  activeFrom: item.value.activeFrom || null,
  activeTo: item.value.activeTo || null,
});
const isLoading = ref(false);

const availableMarkets = ref(defaultRealms);
const availableStates = ['active', 'disabled'];
const availableContexts = ref(defaultSubscriptions);
const availableLanguages = ref([] as Language[]);
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const rules = {
  required: (value: unknown) => !!value || 'This field is required',
  requiredMultiple: (value: unknown[]) => value.length > 0 || 'This field is required',
  notEmptyRule: (val: any) => (val || '').length > 0 || 'This field is required',
  trimmedString: (val: string) =>
    !/^\s+|\s+$|\s{2,}/.test(val) || 'Leading, trailing, or multiple whitespaces in a row are not allowed',
};

// Computed property for mainLanguage binding
const mainLanguage = computed({
  get() {
    return contentCollection.value?.languages?.mainLanguage || '';
  },
  set(value) {
    if (!contentCollection.value?.languages) {
      contentCollection.value.languages = {};
    }
    contentCollection.value.languages.mainLanguage = value;
  },
});

watch(item, () => {
  originalItem.value = cloneDeep(item.value);
  contentCollection.value = item.value;
});

const onSave = async () => {
  // Step 1: Validate the form
  if (!form.value.validate()) return;
  isLoading.value = true;
  // Step 2: Save the form
  if (!isCreate.value) {
    const params = patchCollection(originalItem.value, contentCollection.value);
    if (Object.keys(params).length === 0) {
      isLoading.value = false;
      return;
    }

    await update(contentCollection.value.realm, id?.value, params);
    eventBus.$emit('snackbarV2.open', { text: `Collection #${id?.value} successfully updated !`, color: 'success' });
    isLoading.value = false;
    originalItem.value = cloneDeep(contentCollection.value);
    return;
  }

  const response = await create(contentCollection.value.realm, contentCollection.value);
  router.push({ name: 'collections_edit', params: { id: response.id } });
  eventBus.$emit('snackbarV2.open', { text: `Collection #${id?.value} successfully updated !`, color: 'success' });
  isLoading.value = false;
};

const onActiveFromChanged = (date: string) => {
  //Set time as start of the day
  if (date) {
    const dateObj = new Date(date);
    dateObj.setHours(0, 0, 0, 0); // Set time to start of the day
    contentCollection.value.activeFrom = dateObj.toISOString();
  }
};

const onActiveFromCleared = () => {
  contentCollection.value.activeFrom = null;
};

const onActiveToChanged = (date: string) => {
  //Set time as end of the day
  if (date) {
    const dateObj = new Date(date);
    dateObj.setHours(23, 59, 59, 999); // Set time to end of the day
    contentCollection.value.activeTo = dateObj.toISOString();
  }
};

const onActiveToCleared = () => {
  contentCollection.value.activeTo = null;
};

return { __sfc: true,props, id, title, item, isCreate, isClone, router, CollectionsAvailableIcons, icons, patchCollection, form, isFormValid, originalItem, contentCollection, isLoading, availableMarkets, availableStates, availableContexts, availableLanguages, rules, mainLanguage, onSave, onActiveFromChanged, onActiveFromCleared, onActiveToChanged, onActiveToCleared, CoverArrayInput, RichTextInput, IconPreview }
}

})